import React, {useCallback, useEffect, useState} from "react";
import {isEmpty} from "lodash";
import {clsx} from "clsx";
import {isMobile} from "react-device-detect";
import {useNavigate} from "react-router-dom";

// Store imports
import locationStore from "../../store/locationStore";
import popupStore from "../../store/popupStore";
import addressStore from "../../store/addressStore";
import appStore from "../../store/appStore";

// API imports
import {createAddress, updateAddress} from "../../network/addressApi";
import {fetchAddresses} from "../../services/addressServices";

// Component imports
import FormSuccess from "./FormSuccess";
import ImageBasic from "../Common/Image/ImageBasic";
import Input from "../Common/Input";
import NewSelect from "../Common/Select/NewSelect";
import Button from "../Common/Button";
import CheckboxInput from "../Common/Input/CheckboxInput";
import FutaOrderConfirmationModular from "../shipping/FutaOrderConfirmationModular";
import {ListAddressSeller} from "./ListAddressSeller";
import ToggleSwitch from "../Common/Input/ToggleSwitch";

// Constants and utils
import {VARIANT} from "../../constants";
import {text} from "../../constants/text";
import IMG from "../../constants/image";
import {scrollToTop, validatePhone} from "../../utils";

// Debounce helper
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

export function StoreFormAddress({
                                     data,
                                     isNewAddress,
                                     isOrderConfirm,
                                     handleDeleteAddress,
                                     className,
                                     title,
                                     userName,
                                     placeholderPhone,
                                     placeholderUserName,
                                     customClass,
                                     onUpdateAddress,
                                     addressPickup,
                                     isHideButton
                                 }) {
    // Hooks and Store Access
    const navigate = useNavigate();
    const {provinces, districts, communes, getProvinces, getDistricts, getCommunes} = locationStore();
    const {detailAddress, setDetailAddress} = addressStore();
    const {addressChoice, setAddressChoice, setAnimate, animate, setActiveTabConfirm} = appStore();
    const {setPopup, setPopupMobile} = popupStore() || {};


    // Initial Values From Data
    const {
        name,
        phoneNumber,
        address,
        provinceCode,
        province,
        districtCode,
        district,
        communeCode,
        commune,
        note,
        isPrimary,
        id
    } = data || {};


    // State Management
    const [isSuccess, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        address: '',
        provinceCode: '',
        province: '',
        districtCode: '',
        district: '',
        communeCode: '',
        commune: '',
        note: '',
        isPrimary: false
    });

    const [errors, setErrors] = useState({
        name: '',
        phoneNumber: '',
        address: '',
        provinceCode: '',
        province: '',
        districtCode: '',
        district: '',
        communeCode: '',
        commune: '',
        note: '',
        isPrimary: false
    });

    // Debounced location data fetching
    const debouncedGetDistricts = useCallback(
        debounce((provinceId) => {
            if (provinceId) {
                getDistricts({provinceId});
            }
        }, 300),
        []
    );

    const debouncedGetCommunes = useCallback(
        debounce((districtId) => {
            if (districtId) {
                getCommunes({districtId});
            }
        }, 300),
        []
    );

    // Initial data fetch only once
    useEffect(() => {
        getProvinces();
    }, []);

    // Initialize form data from props
    useEffect(() => {
        if (data && !isNewAddress) {
            setFormData({
                name,
                phoneNumber,
                address,
                provinceCode,
                province,
                districtCode,
                district,
                communeCode,
                commune,
                note,
                isPrimary
            });
        } else {
            setFormData({
                name: '',
                phoneNumber: '',
                address: '',
                provinceCode: '',
                province: '',
                districtCode: '',
                district: '',
                communeCode: '',
                commune: '',
                note: '',
                isPrimary: false
            });
        }
    }, [data]);

    // Optimized location data fetching
    useEffect(() => {
        if (formData?.province && !isEmpty(provinces)) {
            const province = provinces.find(
                (pr) =>
                    pr?.id?.toUpperCase() === formData?.province?.toUpperCase() ||
                    pr?.fullName?.toUpperCase() === formData?.province?.toUpperCase()
            );
            if (province?.code) {
                debouncedGetDistricts(province.code);
            }
        }
    }, [formData?.province, provinces]);

    // Load communes when district changes
    useEffect(() => {
        if (!isEmpty(districts) && formData?.district) {
            const districtValue = String(formData?.district ?? '').toLowerCase();
            const district = districts.find(
                (dis) =>
                    String(dis.id ?? '').toLowerCase() === districtValue ||
                    String(dis.fullName ?? '').toLowerCase() === districtValue ||
                    String(dis.code ?? '').toLowerCase() === districtValue
            );
            if (district?.code) {
                debouncedGetCommunes(district.code);
            }
        }
    }, [districts, formData?.district]);

    // Handle success state
    useEffect(() => {
        if (isSuccess) {
            setPopup({
                isOpen: true,
                data: {
                    customContent: () => <FormSuccess/>,
                }
            });

            setTimeout(() => {
                setPopup({});
                setSuccess(false);

                if (typeof onUpdateAddress === 'function') {
                    return onUpdateAddress();
                }

                if (isMobile) return navigate(-1);
            }, 3000);
        }
    }, [isSuccess]);


    // Event Handlers

    const handleChange = (e) => {
        const {name, value, code} = e.target;
        setErrors({...errors, [name]: ''});

        if (name === 'province') {
            setFormData({...formData, [name]: value, provinceCode: code, district: '', commune: ''});
        } else if (name === 'district') {
            setFormData({...formData, [name]: value, districtCode: code, commune: ''});
        } else if (name === 'commune') {
            setFormData({...formData, [name]: value, communeCode: code});
        } else if (name === 'isPrimary') {
            setFormData({...formData, [name]: value, isPrimary: value});
        } else {
            setFormData({...formData, [name]: value});
        }
    };

    const handleCancel = () => {
        setPopup({});
        setPopupMobile({})
        if (typeof handleDeleteAddress === 'function') {
            return handleDeleteAddress(data?.id);
        }

        if (typeof onUpdateAddress === 'function') {
            return onUpdateAddress();
        }

        if (!isEmpty(detailAddress)) setDetailAddress({});
        if (!isEmpty(addressChoice)) setAddressChoice(null);
    };

    const handleBack = (orderId) => {
        setAnimate({direction: "right"});

        if (isMobile) {
            return setPopupMobile({
                isOpenMobile: true,
                data: {
                    height: "h-[78%]",
                    top: "top-[22%]",
                    children: <ListAddressSeller
                        addressPickup={addressPickup}
                    />
                }
            });
        }

        return setPopup({
            isOpen: true,
            data: {
                buttonCloseClass: 'top-6 right-6',
                customContent: () => <ListAddressSeller
                    addressPickup={addressPickup}
                />
            }
        });
    };

    const handleGetAllAddress = () => {
        setSuccess(true);
        fetchAddresses();
    };


    // Form Validation
    const validateForm = () => {
        const newErrors = {};
        let valid = true;

        if (!formData.name) {
            newErrors.name = text.addressNameRequired;
            valid = false;
        }

        if (!formData.phoneNumber) {
            newErrors.phoneNumber = text.phoneNumberRequired;
            valid = false;
        } else {
            if (!validatePhone(formData.phoneNumber)) {
                newErrors.phoneNumber = text.invalidPhoneNumber;
                valid = false;
            }
        }

        if (!formData.address) {
            newErrors.address = text.addressRequired;
            valid = false;
        }

        if (!formData.province) {
            newErrors.province = text.provinceCityRequired;
            valid = false;
        }

        if (!formData.district) {
            newErrors.district = text.districtRequired;
            valid = false;
        }

        if (!formData.commune) {
            newErrors.commune = text.wardCommuneRequired;
            valid = false;
        }

        if (!valid) {
            scrollToTop();
        }

        setErrors(newErrors);
        return valid;
    };


    // Form Submission
    const handleSubmit = async (orderId) => {
        if (validateForm()) {
            const newFormData = {...formData};
            const idString = id && id?.toString();

            if (isNewAddress) {
                return createAddress(newFormData).then((res) => {
                    if (res.success) {
                        handleGetAllAddress();

                        if (isOrderConfirm) {
                            setAnimate({direction: "right"});

                            if (isMobile) {
                                setActiveTabConfirm('pickup');
                                return setPopupMobile({
                                    isOpenMobile: true,
                                    orderId,
                                    data: {
                                        height: 'h-[90%]',
                                        top: 'top-[12%]',
                                        children: <FutaOrderConfirmationModular
                                            idAddressOrder={res?.data?.id}
                                            onClose={() => setPopupMobile({})}
                                        />
                                    }
                                });
                            }

                            return setPopup({
                                isOpen: true,
                                data: {
                                    bgClass: 'bg-[#FAFAFC]',
                                    buttonCloseClass: 'top-5 right-6',
                                    customContent: () => {
                                        return <FutaOrderConfirmationModular
                                            idAddressOrder={res?.data?.id}
                                            onClose={() => setPopup({})}
                                        />;
                                    }
                                }
                            });
                        }
                    }
                });
            }

            return updateAddress(idString, newFormData).then((res) => {
                if (res.success) {
                    handleGetAllAddress();
                }
            });
        }
    };


    // UI Section Removed

    // The UI components have been moved directly into the render
    // to fix the focus issue with inputs


    // Main Render

    return (
        <form
            className={clsx(
                className ? className : 'w-full md:w-[600px]',
                'flex text-black flex-col rounded-xl md:rounded-none',
                isMobile && animate.direction === 'left' && 'animate-scaleInLeft',
                isMobile && isOrderConfirm ? '' : 'space-y-4 md:space-y-5'
            )}
            action=""
        >
            {/* Header */}
            {isOrderConfirm ? (
                <div className="flex items-center justify-between bg-white px-4 md:px-0 pb-2 pt-6 md:py-0">
                    <ImageBasic
                        className='w-6 h-6 cursor-pointer'
                        src={IMG.icon.chevronLeftBlack}
                        onClick={handleBack}
                        alt={'chevron left'}
                    />
                    <div className="flex justify-center items-center flex-1 space-x-1 bg-white">
                        <ImageBasic className='w-5' src={IMG.icon.location} alt='location'/>
                        <div className='font-bold'>{title}</div>
                    </div>
                </div>
            ) : (
                isMobile ? '' : <div className="hidden md:flex justify-center items-center flex-1 space-x-1 bg-white">
                    <ImageBasic className='w-5' src={IMG.icon.location} alt='location'/>
                    <div className='font-bold'>{title}</div>
                </div>
            )}

            {/* Contact Information */}
            <div className={clsx('bg-white rounded-xl md:p-0',
                isMobile && isOrderConfirm ? 'space-y-[10px] px-4 py-2' : 'space-y-5 p-4')}
            >
                <Input
                    required
                    title={(isMobile && isOrderConfirm) ? text.contact : (userName || text.fullName)}
                    placeholder={placeholderUserName || text.enterFullName}
                    name="name"
                    error={(isMobile && isOrderConfirm) ? '' : errors?.name}
                    errorCustom={(isMobile && isOrderConfirm) ?
                        (errors?.name && errors?.phoneNumber) ?
                            text.pleaseCompleteAllFields :
                            (errors?.name ? errors?.name : (errors?.phoneNumber ? errors?.phoneNumber : ''))
                        : ''}
                    value={formData?.name || ''}
                    onChange={handleChange}
                    valueDefault={text.infoMissing}
                />
                <Input
                    required
                    type='number'
                    title={(isMobile && isOrderConfirm) ? "" : text.phone}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={placeholderPhone || text.phone}
                    valueDefault={text.infoMissing}
                    name="phoneNumber"
                    error={(isMobile && isOrderConfirm) ? '' : errors?.phoneNumber}
                    value={formData?.phoneNumber}
                    onChange={handleChange}
                />
            </div>

            {/* Address Information */}
            <div className={clsx('bg-white rounded-xl md:p-0',
                isMobile && isOrderConfirm ? 'space-y-[10px] px-4 py-2' : 'space-y-5 p-4')}
            >
                <Input
                    required
                    title={text.address}
                    placeholder={text.streetAddress}
                    name="address"
                    error={(isMobile && isOrderConfirm) ? '' : errors?.address}
                    errorCustom={(isMobile && isOrderConfirm) ?
                        (errors?.address && errors?.province && errors?.district && errors?.commune) ?
                            text.pleaseCompleteAllFields :
                            (errors?.address ? errors?.address :
                                errors?.province ? errors?.province :
                                    errors?.district ? errors?.district :
                                        errors?.commune ? errors?.commune : '')
                        : ''}
                    value={formData?.address}
                    onChange={handleChange}
                    valueDefault={text.addressInfoMissing}
                />
                <div className={clsx("grid md:grid-cols-3 text-sm",
                    (isMobile && isOrderConfirm) ? 'space-y-2' : customClass ? customClass : 'gap-5')}
                >
                    <NewSelect
                        isOrderConfirm={isOrderConfirm}
                        isAddress
                        required
                        data={provinces}
                        title={text.provinceCity}
                        placeholder={(isMobile && isOrderConfirm) ? text.provinceCity : text.pleaseSelect}
                        name="province"
                        error={(isMobile && isOrderConfirm) ? '' : errors?.province}
                        value={formData?.province}
                        valueDefault={text.provinceCityInfoMissing}
                        onChange={handleChange}
                    />
                    <NewSelect
                        isOrderConfirm={isOrderConfirm}
                        isAddress
                        required
                        data={districts}
                        placeholder={(isMobile && isOrderConfirm) ? text.districtCounty : text.pleaseSelect}
                        title={text.districtCounty}
                        name="district"
                        error={(isMobile && isOrderConfirm) ? '' : errors?.district}
                        value={formData?.district}
                        valueDefault={text.districtInfoMissing}
                        onChange={handleChange}
                    />
                    <NewSelect
                        isOrderConfirm={isOrderConfirm}
                        isAddress
                        required
                        data={communes}
                        title={text.wardCommune}
                        placeholder={(isMobile && isOrderConfirm) ? text.wardCommune : text.pleaseSelect}
                        name="commune"
                        error={(isMobile && isOrderConfirm) ? '' : errors?.commune}
                        value={formData?.commune}
                        valueDefault={text.wardCommuneInfoMissing}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Notes Information */}
            <div className={clsx('bg-white rounded-xl md:p-0',
                isMobile && isOrderConfirm ? 'space-y-[10px] px-4 py-2' : 'space-y-5 p-4')}
            >
                <Input
                    title={text.notes}
                    placeholder={text.deliveryInstructions}
                    name="note"
                    error={errors?.note}
                    value={formData?.note || ''}
                    onChange={handleChange}
                    valueDefault={text.infoMissing}
                />
            </div>

            {/* Default Address Setting */}
            {isMobile ? (
                <ToggleSwitch
                    className={isOrderConfirm ? 'rounded-xl p-4' : 'rounded-xl'}
                    id="isPrimary"
                    name="isPrimary"
                    title={text.setAsDefaultAddress}
                    onChange={handleChange}
                    toggleSwitch={formData?.isPrimary}
                />
            ) : (
                <CheckboxInput
                    id="isPrimary"
                    name="isPrimary"
                    label={text.setAsDefaultAddress}
                    checked={formData?.isPrimary}
                    onChange={handleChange}
                />
            )}

            {/* Buttons */}
            <div
                className='grid grid-cols-1 md:grid-cols-2 gap-5 text-sm w-full px-4 md:px-0'>
                <Button
                    className='mx-auto w-full'
                    variant={VARIANT.PRIMARY}
                    onClick={handleSubmit}
                >
                    {text.agree}
                </Button>
                {!isHideButton && <Button
                    className={isMobile ? 'w-fit mx-auto text-center bg-transparent text-[#197D55] underline hover:border-none text-sm' : 'mx-auto w-full'}
                    variant={VARIANT.SECONDARY}
                    onClick={handleCancel}
                >
                    {handleDeleteAddress ? text.deleteAddress : text.cancel}
                </Button>}
            </div>
        </form>
    );
}